import { take } from 'rxjs/operators';
import { Component, OnInit } from '@angular/core';
import { MatSelectChange } from '@angular/material/select';
import { SitesService, Site } from '@app/data/services/sitesService';
import { FilterService } from '@app/data/services/filterService';
import { ChartService } from '@app/data/services/chartService';
import { formatDateForApi } from '@app/data/helpers/dataHelpers';
import { EChartsOption } from 'echarts';
import { getHonongaChartConfig, HonongaData } from './rongo-hononga-chart-helpers';
import { RongoReportingData, RongoSourceGroup } from '@app/widgets/helpers/types';
import { withOrganisationBreakdown } from '@app/widgets/helpers/organisation-breakdown';

@Component({
  selector: 'app-rongo-hononga-chart',
  templateUrl: './rongo-hononga-chart.component.html',
  styleUrls: ['./rongo-hononga-chart.component.scss'],
})
export class HonongaCountChartComponent implements OnInit {
  chartTitle: string = 'Rongo Hononga';
  chartSubtitle: string = '';
  chartOptions?: EChartsOption;
  id: string = 'rongo-hononga';
  startDate: any = '';
  endDate: any = '';
  data: HonongaData[] | [] = [];
  availableOptions: string[] = [];
  selectedOptions: string[] = [];
  noData: boolean = true; // We can do this check in html ngIf, but it's hard to read for some widgets
  site: Site | null = null;
  breakdown: RongoSourceGroup<HonongaData[]>[] | null = null;

  constructor(
    private filterService: FilterService,
    private sitesService: SitesService,
    private chartService: ChartService
  ) {}

  ngOnInit() {
    this.startDate = formatDateForApi(this.filterService.getFilters().fromDate);
    this.endDate = formatDateForApi(this.filterService.getFilters().toDate);
    this.filterService.subscribe(this.handleFilterUpdate);
    this.sitesService
      .getSelectedSite()
      .pipe(take(1))
      .subscribe((site) => {
        this.handleSiteUpdate({ site });
        this.sitesService.subscribe(this.handleSiteUpdate);
      });
  }

  setData = () => {
    if (!this.startDate || !this.endDate || !this.site) return;
    this.chartService.getHonongaData(this.startDate, this.endDate, 'organisation').subscribe(
      (data: RongoReportingData<HonongaData[]>) => {
        const { config, data: honongaData, breakdown } = data;
        this.chartTitle = config.title;
        this.chartSubtitle = config.subTitle || '';
        this.data = honongaData;
        this.breakdown = breakdown?.groups || null;
        this.noData = honongaData.length === 0;
        this.availableOptions = this.data.map((item) => item.locationName);
        this.selectedOptions = this.availableOptions;
        this.setChartData();
      },
      (error: any) => {
        console.error('Error fetching data:', error);
      }
    );
  };

  handleSiteUpdate = ({ site }: any) => {
    if (site && site.id != this.site?.id) {
      this.site = site;
      this.setData();
    }
  };

  handleFilterUpdate = (filters: any) => {
    if (!filters.filters.fromDate || !filters.filters.toDate) return; // Bail out until we have full date range

    const newStartDate = formatDateForApi(filters.filters.fromDate);
    const newEndDate = formatDateForApi(filters.filters.toDate);
    const sharedFiltersChanged = this.sharedRongoReportFiltersChanged(filters.changedFilters);
    if (
      (newStartDate !== this.startDate || newEndDate !== this.endDate || sharedFiltersChanged) &&
      !!newStartDate &&
      !!newEndDate
    ) {
      this.startDate = newStartDate;
      this.endDate = newEndDate;
      this.setData();
    }
  };

  private sharedRongoReportFiltersChanged(changedFilters: string[] | undefined): boolean {
    return !!changedFilters?.some((filter) =>
      ['selectedContributingOrganisations', 'selectedContributingProjectListItems'].includes(filter)
    );
  }

  setChartData = () => {
    const filterData = (data: HonongaData[]) => data.filter((item) => this.selectedOptions.includes(item.locationName));

    this.chartOptions = withOrganisationBreakdown(
      getHonongaChartConfig(filterData(this.data as HonongaData[])),
      this.breakdown,
      {
        buildGroupOption: (d) => getHonongaChartConfig(filterData(d)),
        flatten: (d: HonongaData[]) => d.map((item) => ({ name: item.locationName, value: item.totalObservations })),
      }
    );
  };

  handleLocationListChange(event: MatSelectChange): void {
    this.setChartData();
  }
}
